import React from "react";
import { Visit } from "../../@types/PricingModels";
import { UNDEF } from "../../Constants";
import {
  bed,
  BED_TYPES,
  has_manual_transfer,
  home_medical,
  // isAU, // TODO: uncomment in next sprint
  have_hoist,
  manual_transfer_help,
  manual_transfer_help_details,
  require_vital_sign,
  transfer_points,
  transfer_type,
  TRANSFER_TYPES,
  co_accept_2_cps,
  APP_COUNTRY,
  isSG
} from "../../DataConstants";
import { Div, Txt } from "../GenericView";
import { InputField } from "../InputField";
import { isSelected, isNotSelected } from "./VisitDialog";
import "./AdditionalRequirements.css";
const isAU = APP_COUNTRY === "AUS"; // TODO: delete this line in next sprint
interface AdditionalRequirementsProps {
  visit: Visit;
  isMobilityCDEWithSpecificServices: boolean;

  onPropChange: (
    prop: string,
    text: string | undefined,
  ) => void;
  onPropChangeThatProducesArrayOutput: (
    prop: string,
    text: string | undefined
  ) => void;
  onToggle: (visit: any, e: any, prop: string, isSelected?: boolean) => void;

  // disable animations for textfields
  dontAnimate?: boolean;
}

export const AdditionalRequirements: React.FC<AdditionalRequirementsProps> = (
  props
) => {
  const { visit, dontAnimate, onPropChange, onToggle, onPropChangeThatProducesArrayOutput, isMobilityCDEWithSpecificServices } = props;

  const haveHoist = visit[have_hoist];
  const haveHelp = visit[manual_transfer_help];
  const coAccept2CPs = visit[co_accept_2_cps];

  const _renderErrorText = (errorText: string) => {
    return (<Txt className='red-color'><sup>*</sup>{errorText}</Txt>)
  }

  const _showErrorIfManualTransferIsNeitherYesOrNo = () => {
    const manualTransferHelpUnselected = visit.manual_transfer_help === UNDEF;

    if (isSG && manualTransferHelpUnselected && isMobilityCDEWithSpecificServices) {
      return _renderErrorText('Please select an option');
    };
    return null;
  }

  const _showErrorIfManualTransferDetailsNotProvided = () => {
    const manualTransferSelectedWithoutDetails = visit.manual_transfer_help === true && !visit.manual_transfer_help_details;

    if (isSG && manualTransferSelectedWithoutDetails && isMobilityCDEWithSpecificServices) {
      return _renderErrorText('Please provide details');
    };
    return null;
  }

  return (
    <div data-visible="">
      <span className="nvd-mt-label">
        Does the following apply to the visits?
      </span>

      <Div className="h" mt={11}>
        <button
          className="green-border-icon-btn"
          disabled={!!(visit[home_medical] || [])[0]}
          data-selected={isSelected(visit, has_manual_transfer)}
          onClick={(e: any) => onToggle(visit, e, has_manual_transfer)}
        >
          <img src="/manual_transfer_unselected.png" alt="" />
          <span>Manual Transfer</span>
        </button>
        <button
          className="green-border-icon-btn nvd-vital-signs"
          data-selected={isSelected(visit, require_vital_sign)}
          onClick={(e: any) => onToggle(visit, e, require_vital_sign)}
        >
          <img src="/vital-sign-unselected.png" alt="" />
          <span>Vital Signs</span>
        </button>
      </Div>

      <div
        className="nvd-mt-cont vertical"
        data-hid={isNotSelected(visit, has_manual_transfer, false)}
      >
        <Txt className="nvd-mt-label" mt={22}>
          For manual transfer, complete the fields below.
        </Txt>
        {isAU && (
          <div>
            <Txt className="nvd-mt-label" mt={10}>
              Do you have a hoist at home?
            </Txt>

            <Div h mv={11}>
              <button
                className="green-border-btn"
                data-selected={isSelected(visit, have_hoist)}
                onClick={(e: any) => onToggle(visit, e, have_hoist)}
              >
                Yes
              </button>
              <button
                className="green-border-btn nvd-ml11"
                data-selected={isNotSelected(visit, have_hoist)}
                onClick={(e: any) => onToggle(visit, e, have_hoist, false)}
              >
                No
              </button>
            </Div>
          </div>
        )}
        {(!isAU || haveHoist === false) && (
          <>
            <Txt className="nvd-mt-label" mt={10}>
              Additional help: Will there be a person/s present to help with the
              transfer?
            </Txt>

            <Div h mv={11}>
              <button
                className="green-border-btn"
                data-selected={isSelected(visit, manual_transfer_help)}
                onClick={(e: any) => onToggle(visit, e, manual_transfer_help)}
              >
                Yes
              </button>
              <button
                className="green-border-btn nvd-ml11"
                data-selected={isNotSelected(visit, manual_transfer_help)}
                onClick={(e: any) =>
                  onToggle(visit, e, manual_transfer_help, false)
                }
              >
                No
              </button>
            </Div>
          </>
        )}

        {_showErrorIfManualTransferIsNeitherYesOrNo()}

        {(!isAU || (haveHelp && haveHoist === false)) && (
          <InputField
            type="text"
            className="nvd-mt-help"
            width="100%"
            maxWidth="100%"
            noAutoSelect
            lineHeight={19}
            blurOnTabKey
            hidden={!haveHelp}
            dontAnimate={dontAnimate}
            placeholder="e.g Family helper"
            onTextChange={(_: any, t: string) =>
              onPropChange(manual_transfer_help_details, t)
            }
            text={visit[manual_transfer_help_details]}
          />
        )}

        {_showErrorIfManualTransferDetailsNotProvided()}

        {isAU && haveHoist === false && haveHelp === false && (
          <Div h bg="#effafb" pointer br={8} mt={24}>
            <Div
              className={"adr-cpf-checkbox" + (coAccept2CPs ? " selected" : "")}
              h
              mt={6}
              p={13}
              pt={6}
              onClick={(e: any) => onToggle(visit, e, co_accept_2_cps)}
            >
              <i />
              <Txt color="#17A2AC" fs={13} mt={3} ml={8}>
                Care owner accepts that for safety purposes, 2 Care Pros may be
                necessary for this visit if there is no additional help or hoist
                at home.
              </Txt>
            </Div>
          </Div>
        )}

        {(!isAU ||
          haveHoist ||
          (haveHoist === false &&
            (haveHelp || (haveHelp === false && coAccept2CPs)))) && (
            <>
              <Txt className="nvd-mt-label" mt={22} mb={11}>
                Transfer points: State the expected points of transfer. (separate
                with commas)
              </Txt>

              <InputField
                type="text"
                className="nvd-mt-help"
                width="100%"
                maxWidth="100%"
                noAutoSelect
                lineHeight={19}
                dontAnimate={dontAnimate}
                blurOnTabKey
                placeholder="e.g bed to sofa, bed to commode, sofa to commode, etc."
                onTextChange={(_: any, inputText: string) => onPropChangeThatProducesArrayOutput(transfer_points, inputText)
                }
                text={
                  visit[transfer_points] &&
                  (visit[transfer_points] || []).join(", ")
                }
              />

              <Txt className="nvd-mt-label" mt={22} mb={11}>
                Bed type: Select the type of bed the CR uses (transfers are more
                difficult on lower beds).
              </Txt>
              <div className="nvd-mt-bed-types">
                {BED_TYPES.map((type: string) => (
                  <button
                    key={type}
                    className="green-border-icon-btn hcenter"
                    data-selected={visit[bed] === type ? "" : UNDEF}
                    onClick={() =>
                      onPropChange("bed", visit[bed] === type ? UNDEF : type)
                    }
                  >
                    {type}
                  </button>
                ))}
              </div>
            </>
          )}

        {!isAU && (
          <>
            <Txt className="nvd-mt-label" mt={22} mb={11}>
              Recommended type of transfer: Select the type of transfer that
              best suits the CR.
            </Txt>
            <div className="nvd-mt-bed-types">
              {TRANSFER_TYPES.map((tt: any) => (
                <button
                  key={tt.name}
                  className="green-border-icon-btn"
                  data-selected={visit[transfer_type] === tt.name ? "" : UNDEF}
                  onClick={() =>
                    onPropChange(
                      transfer_type,
                      visit[transfer_type] === tt.name ? UNDEF : tt.name
                    )
                  }
                >
                  <img src={tt.unselected_image_url} alt="" />
                  <span>{tt.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface ARInfoProps {
  visit: Visit;
  rootClassName?: string;
  bulletClassName?: string;
}
/**
 * Details to show in VisitDialog, Listview and Visit update popup
 * @param visit
 * @returns ReactElement or Null
 */
export const AdditionalRequirementsInfo: React.FC<ARInfoProps> = ({
  visit,
  rootClassName,
  bulletClassName,
}) => {
  const haveManualTransfer = visit[has_manual_transfer];
  const haveAdditionalRequirements =
    haveManualTransfer || visit[require_vital_sign];

  if (!haveAdditionalRequirements) return null;

  const bulletClsName = bulletClassName || "nvd-bullet";

  // additional help text row
  const additionalHelpSpan = (
    propertyName: string,
    label = "",
    text = "",
    isArray = false,
    className = bulletClsName
  ) => {
    const val = (visit as any)[propertyName];
    const value = isArray && val ? (val.join && val.join(", ")) || val : val;
    return value ? (
      <span className={className}>{label + (text || value)}</span>
    ) : (
      UNDEF
    );
  };

  const haveManualTransferHelp = visit[manual_transfer_help];
  const haveHoist = visit[have_hoist];
  const mtHelpDetails = visit[manual_transfer_help_details];
  const coAccept2CPs = visit[co_accept_2_cps];

  const showHoist = isAU && (haveHoist || haveHoist === false);
  const showMTHelp = haveManualTransferHelp || haveManualTransferHelp === false;

  return (
    <Div className={rootClassName || "nvd-cont"} v relative>
      {additionalHelpSpan(
        has_manual_transfer,
        "",
        "Manual Transfer",
        false,
        "mb"
      )}
      {haveManualTransfer && (
        <>
          {showHoist && (
            <span className={bulletClsName}>
              Hoist available: {haveHoist ? "Yes" : "No"}
            </span>
          )}

          {!haveHoist && showMTHelp && (
            <span className={bulletClsName}>
              Additional help:{" "}
              {haveManualTransferHelp ? mtHelpDetails || "Yes" : "No"}
            </span>
          )}

          {isAU && !haveHoist && coAccept2CPs && (
            <span className="pl-3">CO accepts 2 CPs for the visit</span>
          )}

          {additionalHelpSpan(transfer_points, "Transfer points: ", "", true)}
          {additionalHelpSpan(bed, "Bed type: ")}

          {isAU && (
            <span className={bulletClsName}>
              Recommended type of transfer:{" "}
              {haveHoist ? "Hoist Transfer" : "Two-man Transfer"}
            </span>
          )}

          {!isAU && additionalHelpSpan(transfer_type, "Usual transfer type: ")}
        </>
      )}
      {additionalHelpSpan(require_vital_sign, "", "Vital Signs", false, "mb")}
    </Div>
  );
};

// Adjust manual transfer related fields based on current selection
export function fixManualTransfer(visit: Visit) {
  let toDelete: string[] = [];

  // when manual transfer isn't selected, clear all fields
  if (!visit[has_manual_transfer]) {
    toDelete = [
      manual_transfer_help,
      manual_transfer_help_details,
      transfer_points,
      bed,
      transfer_type,
      have_hoist,
      co_accept_2_cps,
    ];
  } else {
    const haveHoist = visit[have_hoist];
    const haveHelp = visit[manual_transfer_help];

    // MT additional help isn't selected, clear details
    if (!haveHelp) {
      toDelete.push(manual_transfer_help_details);
    }

    if (haveHoist) {
      toDelete = toDelete.concat([
        manual_transfer_help,
        manual_transfer_help_details,
        co_accept_2_cps,
      ]);
    } else if (haveHelp) {
      // No hoist but have additional help, clear co accept 2 cps
      toDelete.push(co_accept_2_cps);
    }
  }

  toDelete.forEach((e) => delete (visit as any)[e]);
}
